import { GLOBAL_SEARCH_GROUP_ORDER } from "../constants/global-search";
import {
  GLOBAL_SEARCH_ITEM_TYPE_NAVIGATION_ICONS,
  GLOBAL_SEARCH_TARGET_NAVIGATION_ICONS,
} from "../constants/navigation-icons";
import type {
  IGlobalSearchGroup,
  IGlobalSearchItem,
  IGlobalSearchTarget,
  IScoreGlobalSearchMatchProps,
  ISearchGlobalSearchStaticEntriesProps,
} from "../types/global-search";
import {
  EGlobalSearchItemType,
  EGlobalSearchTargetKind,
} from "../types/global-search";
import type { ENavigationIcon } from "../types/navigation-icon";

const COMBINING_MARKS_REGEX = /\p{M}/gu;
const WHITESPACE_REGEX = /\s+/g;
const KEYWORD_SEPARATOR = ",";
// NOTE: Between a settings option's area and its tab ("Profile settings ›
// API Keys"). U+203A is bidi-mirrored, so it points the right way in Arabic.
const SECTION_SEPARATOR = " › ";

// NOTE: Match scores, highest wins. A secondary candidate (subtitle, keyword)
// scores a little below the same kind of hit on the title, so "Theme" the
// setting outranks a page that only lists "theme" as a synonym.
const SCORE_EXACT = 100;
const SCORE_PREFIX = 80;
const SCORE_WORD_PREFIX = 60;
const SCORE_SUBSTRING = 40;
const SCORE_ALL_TOKENS = 20;
const SECONDARY_CANDIDATE_PENALTY = 5;

// NOTE: Case-, accent- and whitespace-insensitive, and folds Russian "ё" into
// "е" — users type "еще" for "ещё" far more often than not.
export const normalizeGlobalSearchText = (value: string): string =>
  value
    .normalize("NFD")
    .replace(COMBINING_MARKS_REGEX, "")
    .toLowerCase()
    .replaceAll("ё", "е")
    .replace(WHITESPACE_REGEX, " ")
    .trim();

const scoreCandidate = (query: string, candidate: string): number => {
  if (candidate.length === 0) return 0;
  if (candidate === query) return SCORE_EXACT;
  if (candidate.startsWith(query)) return SCORE_PREFIX;
  if (candidate.split(" ").some((word) => word.startsWith(query))) {
    return SCORE_WORD_PREFIX;
  }
  if (candidate.includes(query)) return SCORE_SUBSTRING;

  const tokens = query.split(" ").filter((token) => token.length > 0);
  if (tokens.length > 1 && tokens.every((token) => candidate.includes(token))) {
    return SCORE_ALL_TOKENS;
  }

  return 0;
};

// NOTE: 0 means "no match". The first candidate is the primary label.
export const scoreGlobalSearchMatch = ({
  query,
  candidates,
}: IScoreGlobalSearchMatchProps): number => {
  const normalizedQuery = normalizeGlobalSearchText(query);
  if (normalizedQuery.length === 0) return 0;

  let best = 0;
  for (const [index, raw] of candidates.entries()) {
    if (raw === null || raw === undefined) continue;
    const score = scoreCandidate(
      normalizedQuery,
      normalizeGlobalSearchText(raw),
    );
    if (score === 0) continue;
    const weighted = index === 0 ? score : score - SECONDARY_CANDIDATE_PENALTY;
    best = Math.max(best, weighted);
  }

  return best;
};

// NOTE: Pages, settings tabs and settings options live in a static index; this
// turns the entries a user can open into ranked palette items. The target is
// flattened into `params` (`targetKind` + its params) so a static item travels
// in the same IGlobalSearchItem shape as a server one.
export const searchGlobalSearchStaticEntries = ({
  query,
  entries,
  translate,
  isGateOpen,
  limit,
}: ISearchGlobalSearchStaticEntriesProps): IGlobalSearchItem[] =>
  entries
    .filter((entry) => entry.gates.every((gate) => isGateOpen(gate)))
    .map((entry) => {
      const title = translate(entry.titleKey);
      const subtitle =
        entry.subtitleKey === undefined ? null : translate(entry.subtitleKey);
      const shownSubtitle =
        subtitle === null || entry.sectionKey === undefined
          ? subtitle
          : `${translate(entry.sectionKey)}${SECTION_SEPARATOR}${subtitle}`;
      const keywords = (entry.keywordKeys ?? []).flatMap((key) =>
        translate(key).split(KEYWORD_SEPARATOR),
      );
      const score = scoreGlobalSearchMatch({
        query,
        candidates: [title, subtitle, ...keywords],
      });

      return { entry, title, subtitle: shownSubtitle, score };
    })
    .filter((match) => match.score > 0)
    .sort((left, right) => right.score - left.score)
    .slice(0, limit)
    .map(({ entry, title, subtitle }) => ({
      type: entry.type,
      id: entry.id,
      title,
      subtitle,
      params: { ...entry.target.params, targetKind: entry.target.kind },
    }));

export const sortGlobalSearchGroups = (
  groups: readonly IGlobalSearchGroup[],
): IGlobalSearchGroup[] => {
  const rank = (type: EGlobalSearchItemType): number => {
    const index = GLOBAL_SEARCH_GROUP_ORDER.indexOf(type);
    return index === -1 ? GLOBAL_SEARCH_GROUP_ORDER.length : index;
  };

  // NOTE: `filter` already returns a fresh array, so sorting it in place leaves
  // the caller's `groups` untouched. Not `toSorted`: the Hermes engine the
  // mobile app ships has no Array.prototype.toSorted, and this runs on every
  // render of the mobile palette (NSAI-6590).
  return groups
    .filter((group) => group.items.length > 0)
    .sort((left, right) => rank(left.type) - rank(right.type));
};

const isTargetKind = (value: string): value is EGlobalSearchTargetKind =>
  Object.values<string>(EGlobalSearchTargetKind).includes(value);

const target = (
  kind: EGlobalSearchTargetKind,
  params: Record<string, string> = {},
): IGlobalSearchTarget => ({ kind, params });

const pickParams = (
  params: Record<string, string>,
  keys: readonly string[],
): Record<string, string> =>
  Object.fromEntries(
    keys
      .map((key) => [key, params[key]] as const)
      .filter(
        (pair): pair is readonly [string, string] => pair[1] !== undefined,
      ),
  );

// NOTE: The one place a result becomes a destination. `params` keys per type —
// server sources must emit exactly these:
//   PROMPT                  folderUuid
//   CUSTOM_TOOL             customIntegrationUuid
//   INTEGRATION_CONNECTION  provider
//   AUTOMATION              automationType ("cron" | "webhook" | "hook")
//   KNOWLEDGE_FILE          tab ("my-files" | "shared-with-me")
//   CALENDAR_EVENT          meetingUuid (optional)
//   GENERATED_FILE          chatUuid + messageUuid | automationUuid | isHtmlPage="true"
//   MEMBER / INVITE / ROLE  organizationUuid, projectUuid (optional)
//   API_KEY                 scope ("organization" | "personal" | "syncator"),
//                           organizationUuid / projectUuid for the org scopes
//   CHAT                    — (id is the chat uuid)
//   CHAT_MESSAGE            chatUuid (id is the message uuid)
//   PAGE / SETTINGS_*       targetKind + the target's own params
//   INTEGRATION_PROVIDER    — (id is the provider key)
//   PROJECT                 — (id is the project uuid)
//   ORGANIZATION            — (id is the organization uuid)
//   FAQ_ITEM                — (id is the FAQ item id)
//   NOTIFICATION            — (id is the notification uuid)
//   ACTION                  targetKind (+ query)
// Every other type needs only its `id`.
export const getGlobalSearchItemTarget = (
  item: IGlobalSearchItem,
): IGlobalSearchTarget | null => {
  const { id, params } = item;

  switch (item.type) {
    case EGlobalSearchItemType.ASSISTANT:
      return target(EGlobalSearchTargetKind.ASSISTANT, { assistantUuid: id });
    case EGlobalSearchItemType.PROMPT:
      return target(EGlobalSearchTargetKind.PROMPT_LIBRARY, {
        promptUuid: id,
        ...pickParams(params, ["folderUuid"]),
      });
    case EGlobalSearchItemType.CUSTOM_TOOL:
      return params.customIntegrationUuid === undefined
        ? null
        : target(EGlobalSearchTargetKind.CUSTOM_INTEGRATION, {
            customIntegrationUuid: params.customIntegrationUuid,
          });
    case EGlobalSearchItemType.CUSTOM_INTEGRATION:
      return target(EGlobalSearchTargetKind.CUSTOM_INTEGRATION, {
        customIntegrationUuid: id,
      });
    case EGlobalSearchItemType.INTEGRATION_CONNECTION:
      return params.provider === undefined
        ? target(EGlobalSearchTargetKind.INTEGRATIONS, { tab: "connected" })
        : target(EGlobalSearchTargetKind.INTEGRATION_DETAILS, {
            provider: params.provider,
          });
    case EGlobalSearchItemType.INTEGRATION_PROVIDER:
      return target(EGlobalSearchTargetKind.INTEGRATION_DETAILS, {
        provider: id,
      });
    case EGlobalSearchItemType.SKILL:
      return target(EGlobalSearchTargetKind.SKILL, { skillUuid: id });
    case EGlobalSearchItemType.CATALOG_SKILL:
      return target(EGlobalSearchTargetKind.SKILL_CATALOG_ENTRY, {
        entryUuid: id,
      });
    case EGlobalSearchItemType.AUTOMATION:
      return target(EGlobalSearchTargetKind.AGENTS, { runs: id });
    case EGlobalSearchItemType.MARKETPLACE_AUTOMATION:
      return target(EGlobalSearchTargetKind.AGENTS, { tab: "marketplace" });
    case EGlobalSearchItemType.MODEL:
      return target(EGlobalSearchTargetKind.MODELS, { modelUuid: id });
    case EGlobalSearchItemType.KNOWLEDGE_FILE:
      return target(EGlobalSearchTargetKind.KNOWLEDGE, {
        fileUuid: id,
        tab: params.tab ?? "my-files",
      });
    case EGlobalSearchItemType.MEETING:
      return target(EGlobalSearchTargetKind.MEETING, { meetingUuid: id });
    case EGlobalSearchItemType.CALENDAR_EVENT:
      return params.meetingUuid === undefined
        ? target(EGlobalSearchTargetKind.CALL_ASSISTANT)
        : target(EGlobalSearchTargetKind.MEETING, {
            meetingUuid: params.meetingUuid,
          });
    case EGlobalSearchItemType.GENERATED_FILE:
      if (params.isHtmlPage === "true") {
        return target(EGlobalSearchTargetKind.HTML_PREVIEW, { fileUuid: id });
      }
      if (params.chatUuid !== undefined) {
        return target(EGlobalSearchTargetKind.CHAT, {
          chatUuid: params.chatUuid,
          ...pickParams(params, ["messageUuid"]),
        });
      }
      if (params.automationUuid !== undefined) {
        return target(EGlobalSearchTargetKind.AGENTS, {
          runs: params.automationUuid,
        });
      }
      return target(EGlobalSearchTargetKind.GENERATED_FILES);
    case EGlobalSearchItemType.DOC_RECOGNITION_DOCUMENT:
      return target(EGlobalSearchTargetKind.DOC_RECOGNITION_DOCUMENT, {
        documentUuid: id,
      });
    case EGlobalSearchItemType.CHAT_FOLDER:
      return target(EGlobalSearchTargetKind.CHAT_FOLDERS, { folderUuid: id });
    case EGlobalSearchItemType.MEMBER:
    case EGlobalSearchItemType.INVITE:
    case EGlobalSearchItemType.ROLE:
      return params.organizationUuid === undefined
        ? null
        : target(EGlobalSearchTargetKind.SETTINGS, {
            section: "organization",
            tab: item.type === EGlobalSearchItemType.ROLE ? "roles" : "members",
            highlight: id,
            ...pickParams(params, ["organizationUuid", "projectUuid"]),
          });
    case EGlobalSearchItemType.API_KEY:
      if (params.scope === "personal") {
        return target(EGlobalSearchTargetKind.SETTINGS, {
          section: "profile",
          tab: "apiKeys",
          highlight: id,
        });
      }
      return params.organizationUuid === undefined
        ? null
        : target(EGlobalSearchTargetKind.SETTINGS, {
            section: "organization",
            tab: params.scope === "syncator" ? "syncator_api_keys" : "api_keys",
            highlight: id,
            ...pickParams(params, ["organizationUuid", "projectUuid"]),
          });
    case EGlobalSearchItemType.MEMORY_FACT:
      return target(EGlobalSearchTargetKind.SETTINGS, {
        section: "profile",
        tab: "memory",
        highlight: id,
      });
    case EGlobalSearchItemType.CUSTOM_INSTRUCTIONS:
      return target(EGlobalSearchTargetKind.SETTINGS, {
        section: "profile",
        tab: "memoryInstructions",
      });
    case EGlobalSearchItemType.PROBE:
      return target(EGlobalSearchTargetKind.ANALYTICS, {
        tab: "probes",
        probeUuid: id,
      });
    case EGlobalSearchItemType.CHAT:
      return target(EGlobalSearchTargetKind.CHAT, { chatUuid: id });
    case EGlobalSearchItemType.CHAT_MESSAGE:
      return params.chatUuid === undefined
        ? null
        : target(EGlobalSearchTargetKind.CHAT, {
            chatUuid: params.chatUuid,
            messageUuid: id,
          });
    case EGlobalSearchItemType.PROJECT:
      return target(EGlobalSearchTargetKind.SWITCH_PROJECT, {
        projectUuid: id,
      });
    case EGlobalSearchItemType.ORGANIZATION:
      return target(EGlobalSearchTargetKind.SETTINGS, {
        section: "organization",
        organizationUuid: id,
      });
    case EGlobalSearchItemType.FAQ_ITEM:
      return target(EGlobalSearchTargetKind.FAQ, { itemId: id });
    case EGlobalSearchItemType.NOTIFICATION:
      return target(EGlobalSearchTargetKind.OPEN_NOTIFICATION, {
        notificationUuid: id,
      });
    case EGlobalSearchItemType.PAGE:
    case EGlobalSearchItemType.SETTINGS_TAB:
    case EGlobalSearchItemType.SETTINGS_OPTION:
    case EGlobalSearchItemType.ACTION: {
      const { targetKind, ...targetParams } = params;
      return targetKind !== undefined && isTargetKind(targetKind)
        ? target(targetKind, targetParams)
        : null;
    }
    default:
      return null;
  }
};

// NOTE: NSAI-6812 — the sidebar icon a result shares with its destination, or
// undefined when the destination has no sidebar entry (the platform then shows
// the result type's own icon). A page / settings result goes by its target, a
// record by the section its type belongs to. Actions are commands, not
// destinations, so they keep their own icon.
export const getGlobalSearchItemNavigationIcon = (
  item: IGlobalSearchItem,
): ENavigationIcon | undefined => {
  const { targetKind } = item.params;
  const targetIcon =
    targetKind !== undefined && isTargetKind(targetKind)
      ? GLOBAL_SEARCH_TARGET_NAVIGATION_ICONS[targetKind]
      : undefined;

  return item.type === EGlobalSearchItemType.ACTION
    ? undefined
    : (targetIcon ?? GLOBAL_SEARCH_ITEM_TYPE_NAVIGATION_ICONS[item.type]);
};
